import { useTranslation } from 'react-i18next';

import { Badge, Button } from '@saas-kit/ui';

import type { AdminPayment } from './types';

interface Props {
  payments: AdminPayment[];
  currency: string;
  isVoiding: boolean;
  voidingId: string | null;
  onVoid: (paymentId: string) => void;
}

function formatMoney(amount: string, currency: string): string {
  return `${amount} ${currency}`;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function PaymentsList({
  payments,
  currency,
  isVoiding,
  voidingId,
  onVoid,
}: Props): JSX.Element {
  const { t } = useTranslation('adminBilling');
  if (payments.length === 0) {
    return (
      <section>
        <h3 className="mb-2 text-sm font-medium">{t('detail.payments')}</h3>
        <p className="text-sm text-muted-foreground">{t('detail.no_payments')}</p>
      </section>
    );
  }
  return (
    <section>
      <h3 className="mb-2 text-sm font-medium">{t('detail.payments')}</h3>
      <ul className="divide-y divide-border rounded-md border border-border">
        {payments.map((payment) => (
          <li
            key={payment.id}
            className="flex items-center justify-between px-3 py-2"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">
                  {formatMoney(payment.amount, currency)}
                </span>
                <Badge
                  variant={payment.status === 'void' ? 'outline' : 'secondary'}
                >
                  {t(`payment.methods.${payment.payment_method}`)}
                </Badge>
                {payment.status === 'void' ? (
                  <Badge variant="destructive">{t('detail.voided')}</Badge>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatDate(payment.received_at)}
                {payment.reference ? ` · ${payment.reference}` : ''}
              </p>
            </div>
            {payment.status !== 'void' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={isVoiding && voidingId === payment.id}
                onClick={() => onVoid(payment.id)}
              >
                {t('detail.actions.void_payment')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
